
import React from 'react';
import { Page } from '@/types/page';
import { UnifiedDisplayRenderer } from './UnifiedDisplayRenderer';
import DailyStudyPage from '../pages/DailyStudyPage';
import PrayerTimesPage from '../pages/PrayerTimesPage';
import MemorialPage from '../pages/MemorialPage';
import CustomPage from '../pages/CustomPage';
import IframePage from '../pages/IframePage';
import RssFeedPage from '../pages/RssFeedPage';
import SupabaseDataPage from '../pages/SupabaseDataPage';
import VideoPage from '../pages/VideoPage';
import PresetApiPage from '../pages/PresetApiPage';
import TimesPage from '../pages/TimesPage';
import ZmanimCardsPage from '../pages/ZmanimCardsPage';
import YahrzeitDisplayPage from '../pages/YahrzeitDisplayPage';

interface DisplayContentProps {
  fadeState: "fade-enter" | "fade-exit" | "";
  isHebrew: boolean;
  currentLeftPage: Page | null;
  currentRightPage: Page | null;
  currentFullscreenPage: Page | null;
  isMobile: boolean;
}

const DisplayContent: React.FC<DisplayContentProps> = ({
  fadeState,
  isHebrew,
  currentLeftPage,
  currentRightPage,
  currentFullscreenPage,
  isMobile,
}) => {
  // Log component render and props for debugging
  React.useEffect(() => {
    console.log("DisplayContent rendered with pages:", { 
      leftPage: currentLeftPage?.type,
      rightPage: currentRightPage?.type,
      fullscreenPage: currentFullscreenPage?.type,
      isHebrew,
      fadeState
    });
  }, [currentLeftPage, currentRightPage, currentFullscreenPage, isHebrew, fadeState]);
  
  const renderPage = (page: Page | null) => {
    if (!page) return null;

    console.log(`Rendering page of type: ${page.type}`);
    
    try {
      switch (page.type) {
        case "daily-study":
          return <DailyStudyPage content={page.content} />;
        case "prayer-times":
          return <PrayerTimesPage content={page.content} />;
        case "memorial":
          return <MemorialPage content={page.content} />;
        case "iframe":
          return <IframePage content={page.content} />;
        case "custom":
          return <CustomPage content={page.content} />;
        case "rss":
          return <RssFeedPage content={page.content} />;
        case "supabase-data":
          return <SupabaseDataPage content={page.content} isHomePage={true} />;
        case "video":
          return <VideoPage content={page.content} />;
        case "preset-api":
          return <PresetApiPage content={page.content} isHomePage={true} />;
        case "times":
          return <TimesPage content={page.content} />;
        case "zmaney-hayom":
          return <ZmanimCardsPage selectedZmanim={page.content?.selectedZmanim} />;
        case "yahrzeit_display":
          return <YahrzeitDisplayPage content={page.content} />;
        default:
          console.warn(`Unsupported page type: ${page.type}`);
          return <div>Page type not supported</div>;
      }
    } catch (error) {
      console.error(`Error rendering page of type ${page.type}:`, error);
      return <div>Error rendering page</div>;
    }
  };

  // Check if we should use unified display (when there are multiple enabled pages)
  const allPages = [currentLeftPage, currentRightPage, currentFullscreenPage].filter(Boolean) as Page[];
  const shouldUseUnifiedDisplay = allPages.length > 1;

  // Use unified display for multiple pages
  if (shouldUseUnifiedDisplay) {
    return (
      <div className={`page-container ${fadeState} min-h-full w-full overflow-hidden`}>
        <div className="w-full h-full bg-gradient-to-br from-white/25 via-white/15 to-white/10 backdrop-blur-xl rounded-2xl p-3 sm:p-4 md:p-6 shadow-2xl border border-white/20 overflow-hidden">
          <UnifiedDisplayRenderer 
            pages={allPages}
            rotationInterval={8}
            autoRotate={true}
          />
        </div>
      </div>
    );
  }

  // Fullscreen page view
  if (currentFullscreenPage) {
    return (
      <div className={`page-container ${fadeState} min-h-full w-full overflow-hidden`}>
        <div className="w-full h-full bg-gradient-to-br from-white/25 via-white/15 to-white/10 backdrop-blur-xl rounded-2xl p-3 sm:p-4 md:p-6 shadow-2xl border border-white/20 overflow-auto">
          <div className="max-w-full h-full">
            {renderPage(currentFullscreenPage)}
          </div>
        </div>
      </div>
    );
  }

  // Mobile view - stack the pages vertically with improved spacing
  if (isMobile && currentLeftPage && currentRightPage) {
    return (
      <div className={`page-container ${fadeState} min-h-full flex flex-col space-y-3 sm:space-y-4 p-3 sm:p-4 overflow-hidden`}>
        {/* First page */}
        <div className="w-full flex-1 bg-gradient-to-br from-white/25 via-white/15 to-white/10 backdrop-blur-xl rounded-2xl p-3 sm:p-4 shadow-2xl border border-white/20 overflow-auto">
          <div className="relative w-full h-full max-w-full">
            {renderPage(isHebrew ? currentRightPage : currentLeftPage)}
          </div>
        </div>
        
        {/* Enhanced divider */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/40 to-transparent shadow-sm"></div>
        
        {/* Second page */}
        <div className="w-full flex-1 bg-gradient-to-br from-white/25 via-white/15 to-white/10 backdrop-blur-xl rounded-2xl p-3 sm:p-4 shadow-2xl border border-white/20 overflow-auto">
          <div className="relative w-full h-full max-w-full">
            {renderPage(isHebrew ? currentLeftPage : currentRightPage)}
          </div>
        </div>
      </div>
    );
  }

  // Desktop view - side by side columns with improved styling
  return (
    <div className={`page-container ${fadeState} min-h-full flex ${isHebrew ? 'flex-row-reverse' : 'flex-row'} justify-between p-3 sm:p-4 overflow-hidden gap-3 sm:gap-4`}>
      {/* Left Column */}
      <div 
        className={`
          ${(currentLeftPage && currentRightPage) ? 'w-1/2' : 'w-full'}
          bg-gradient-to-br from-white/25 via-white/15 to-white/10
          backdrop-blur-xl rounded-2xl p-3 sm:p-4 md:p-6
          shadow-2xl border border-white/20
          ${!currentLeftPage ? 'hidden' : ''}
          overflow-auto h-full
          transition-all duration-300 ease-in-out
          hover:shadow-3xl hover:bg-white/30
        `}
        tabIndex={0}
      >
        {currentLeftPage && (
          <div className="relative w-full h-full max-w-full">
            {renderPage(currentLeftPage)}
          </div>
        )}
      </div>
      
      {/* Right Column */}
      <div 
        className={`
          ${(currentLeftPage && currentRightPage) ? 'w-1/2' : 'w-full'}
          bg-gradient-to-br from-white/25 via-white/15 to-white/10
          backdrop-blur-xl rounded-2xl p-3 sm:p-4 md:p-6
          shadow-2xl border border-white/20
          ${!currentRightPage ? 'hidden' : ''}
          overflow-auto h-full
          transition-all duration-300 ease-in-out
          hover:shadow-3xl hover:bg-white/30
        `}
        tabIndex={0}
      >
        {currentRightPage && (
          <div className="relative w-full h-full max-w-full">
            {renderPage(currentRightPage)}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisplayContent;
